import type { InitConfig } from "./main";
import type { WidgetProps } from "./ws-element/elements/WebComponent";
import type Websail from "./main";

const packageVersion = import.meta.env.BEE_PACKAGE_VERSION;

const version = {
  VERSION: import.meta.env.BEE_VERSION.includes(packageVersion)
    ? import.meta.env.BEE_VERSION
    : import.meta.env.BEE_VERSION + `(${packageVersion})`,
  LAST_COMMIT_DATE_TIME: import.meta.env.BEE_LAST_COMMIT_DATE_TIME,
  COMMIT_HASH: import.meta.env.BEE_COMMIT_HASH,
};

const isLegacy =
  window.location.search.includes("legacy=true") ||
  new URL(
    window.location.hash.substring(1),
    window.location.origin,
  ).search.includes("legacy=true");

const widgetStore = new Map<string, Promise<WidgetProps[]>>();

function loadWidgets(apiBaseUrl: string) {
  if (isLegacy) return;

  const url = new URL("/api/widgets", apiBaseUrl);

  if (!widgetStore.get(url.href)) {
    widgetStore.set(
      url.href,
      fetch(url.href, {
        headers: { Accept: "application/json" },
      })
        .then((res) => res.json() as Promise<{ body: WidgetProps[] }>)
        .then((result) => result.body)
        .catch(() => []),
    );
  }

  return widgetStore.get(url.href)!;
}

class WebsailLoader {
  get VERSION() {
    return version.VERSION;
  }

  get LAST_COMMIT_DATE() {
    return version.LAST_COMMIT_DATE_TIME;
  }

  args = [] as Array<InitConfig>;
  constructorArgs = [] as Array<InitConfig>;
  isAdmin = false;

  static async getIntegration(integrationName: string) {
    let integration =
      window.wsBundleIntegration.getIntegration(integrationName);

    if (integration?.apiBaseUrl) return integration;

    return new Promise<Websail>((resolve) => {
      window.addEventListener("ws-integration-init", () => {
        integration =
          window.wsBundleIntegration.getIntegration(integrationName);

        if (integration?.apiBaseUrl) resolve(integration);
      });
    });
  }

  static async fetchWidgets(integrationName: string) {
    const integration = await this.getIntegration(integrationName);
    const apiBaseURL = integration?.apiBaseUrl;

    if (!apiBaseURL) {
      return [];
    }

    return loadWidgets(apiBaseURL) ?? [];
  }

  onLoadBundle = () => {
    for (const constructorArgs of this.constructorArgs) {
      const integration = new window.wsBundleIntegration(constructorArgs);
      integration.init(this.args.shift());
    }

    this.init(this.args.shift());
  };

  loadScripts = (config?: InitConfig) => {
    if (config?.log) {
      this.#log();
    }

    if (!config?.apiBaseUrl) {
      return;
    }

    if (window.websailBundle) {
      return;
    }

    let basePath = config.apiBaseUrl;
    let pathname = isLegacy ? "/bee-react-form/legacy/" : "/bee-react-form/";

    if (config.publicUrl) {
      basePath = URL.parse(config.publicUrl)?.href || basePath;
      pathname = URL.parse(config.publicUrl)?.pathname || "";
      if (!pathname.endsWith("/")) {
        pathname += "/";
      }
    }

    loadWidgets(config.apiBaseUrl);

    if (basePath.indexOf("?") >= 0) {
      basePath = basePath.slice(0, basePath.indexOf("?"));
    }

    let filePath = new URL(`${pathname}bundle.min.js`, basePath).href;
    filePath += "?v=" + this.VERSION;

    if (import.meta.env.MODE === "development") {
      return import("./main").then(() => {
        this.onLoadBundle();
      });
    }

    if (document.getElementById("ws-integration-bundle-script")) {
      return;
    }

    const sNew = document.createElement("script");
    sNew.id = "ws-integration-bundle-script";
    sNew.onload = this.onLoadBundle;
    sNew.async = true;
    sNew.type = "module";
    sNew.crossOrigin = "anonymous";
    sNew.src = filePath;

    document.body.append(sNew);
  };

  constructor(config?: Partial<InitConfig> & { loader?: boolean }) {
    if (config?.loader !== true && config?.apiBaseUrl) {
      loadWidgets(config.apiBaseUrl);

      if (config) this.constructorArgs.push(config as InitConfig);

      if (window.wsBundleIntegration) {
        this.onLoadBundle();
        return;
      }
    }

    const websailConfig = window.websailConfig;

    if (websailConfig && "apiBaseUrl" in websailConfig) {
      this.args.push(websailConfig);
    }

    this.loadScripts({ ...config, ...websailConfig } as InitConfig);
  }

  #log() {
    console.log(
      `Websail:\n\n%cVersion:\t\t${this.VERSION}\n%cLast Commit:\t${this.LAST_COMMIT_DATE}\n%cCommit Hash:\t${version.COMMIT_HASH}`,
      "color: #4CAF50; font-weight: bold;",
      "color: #4CAF50; font-weight: bold;",
      "color: #4CAF50; font-weight: bold;",
    );
  }

  init = (config?: InitConfig) => {
    if (!config) {
      return;
    }

    this.loadScripts(config);

    const wsBundleIntegration = window.wsBundleIntegration;
    if (wsBundleIntegration) {
      this.args.unshift(config);
      new wsBundleIntegration(this.args.shift()).init();
      return;
    }

    this.args.push(config);
  };

  initAdmin() {
    this.isAdmin = true;
  }

  onRedirect: undefined | ((url: string) => void);
}

if (!window.wsintegration) {
  window.wsintegration = WebsailLoader;
  window.websail = new WebsailLoader({ loader: true });
}

declare global {
  interface Window {
    websail: WebsailLoader;
    wsintegration: typeof WebsailLoader;
    websailConfig?: InitConfig;
  }
}

export default {};
